import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { scanForImageUsage } from '../utils/imageScanner';
import { toast } from 'react-hot-toast';

interface AppSettings {
  general: {
    siteName: string;
    siteDescription: string;
    language: string;
    timezone: string;
  };
  appearance: {
    theme: 'light' | 'dark' | 'system';
    accentColor: string;
    fontSize: string;
    animations: boolean;
    customCss: string;
  };
  notifications: {
    email: boolean;
    push: boolean;
    desktop: boolean;
    sound: boolean;
    frequency: 'realtime' | 'hourly' | 'daily';
    quietHours: {
      enabled: boolean;
      start: string;
      end: string;
    };
  };
  security: {
    twoFactorAuth: boolean;
    sessionTimeout: number;
    passwordMinLength: number;
    passwordRequirements: {
      uppercase: boolean;
      numbers: boolean;
      symbols: boolean;
    };
    loginAttempts: number;
  };
  media: {
    maxUploadSize: number;
    allowedFileTypes: string[];
    imageCompression: boolean;
    defaultImageQuality: number;
    autoOptimize: boolean;
    storageQuota: number;
    imageUrls?: {
      [key: string]: string;
    };
  };
  backup: {
    autoBackup: boolean;
    backupFrequency: string;
    retentionPeriod: number;
    includeMedia: boolean;
    encryptBackups: boolean;
    backupLocations: string[];
  };
  analytics: {
    enabled: boolean;
    trackPageViews: boolean;
    trackEvents: boolean;
    anonymizeData: boolean;
    retentionDays: number;
    excludedPaths: string[];
  };
}

export function useSettings() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageUsage, setImageUsage] = useState<{[key: string]: string[]}>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadSettings();
    loadImageUsage();
  }, [refreshTrigger]);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .single();

      if (error) throw error;
      setSettings(data?.settings || getDefaultSettings());
    } catch (error) {
      console.error('Error loading settings:', error);
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  };

  const loadImageUsage = async () => {
    try {
      const usage = await scanForImageUsage();
      setImageUsage(usage);
    } catch (error) {
      console.error('Error scanning for images:', error);
      toast.error('Erro ao carregar uso de imagens');
    }
  };

  const updateSettings = async (newSettings: AppSettings) => {
    try {
      // Get all settings records
      const { data: allSettings, error: fetchError } = await supabase
        .from('app_settings')
        .select('*');

      if (fetchError) {
        console.error('Error fetching settings:', fetchError);
        throw fetchError;
      }

      // Get the first record or create a new UUID
      const settingsId = allSettings?.[0]?.id || crypto.randomUUID();

      // Upsert the settings
      const { error } = await supabase
        .from('app_settings')
        .update({ settings: newSettings })
        .eq('id', settingsId);

      if (error) throw error;
      setSettings(newSettings);
      // Trigger a refresh of image usage
      await loadImageUsage();
      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  const resetSettings = async () => {
    try {
      const defaultSettings = getDefaultSettings();
      await updateSettings(defaultSettings);
      setSettings(defaultSettings);
      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
      return true;
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error;
    }
  };

  return {
    settings,
    loading,
    imageUsage,
    updateSettings,
    resetSettings
  };
}

function getDefaultSettings(): AppSettings {
  return {
    general: {
      siteName: 'SINOPSE',
      siteDescription: 'Portal de Podcasts e Notícias do AEAAV',
      language: 'pt',
      timezone: 'Europe/Lisbon'
    },
    appearance: {
      theme: 'dark',
      accentColor: '#0095FF',
      fontSize: 'medium',
      animations: true,
      customCss: ''
    },
    notifications: {
      email: true,
      push: true,
      desktop: true,
      sound: true,
      frequency: 'realtime',
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '08:00'
      }
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordMinLength: 8,
      passwordRequirements: {
        uppercase: true,
        numbers: true,
        symbols: true
      },
      loginAttempts: 5
    },
    media: {
      maxUploadSize: 50,
      allowedFileTypes: ['.jpg', '.png', '.gif', '.mp3', '.mp4'],
      imageCompression: true,
      defaultImageQuality: 80,
      autoOptimize: true,
      storageQuota: 5120,
      imageUrls: {
        project_hero: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
        momentos_1: 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-13-as-19.48.01_077350e7.jpg',
        momentos_2: 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-02-24-as-14.00.53_7373e59b.jpg',
        momentos_3: 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-15-as-17.37.03_ce0a38f1.jpg'
      }
    },
    backup: {
      autoBackup: true,
      backupFrequency: 'daily',
      retentionPeriod: 30,
      includeMedia: true,
      encryptBackups: true,
      backupLocations: ['local', 'cloud']
    },
    analytics: {
      enabled: true,
      trackPageViews: true,
      trackEvents: true,
      anonymizeData: true,
      retentionDays: 90,
      excludedPaths: []
    }
  };
}